export type UserRole = 'student' | 'admin';
export type MainCategory = 'school' | 'academic' | 'crc' | 'skill';

export interface SubCategory {
  id: string;
  name: string;
  mainCategory: MainCategory;
  createdAt: string;
}

export interface UserProfile {
  uid: string;
  email: string;
  displayName: string;
  role: UserRole;
  enrolledCourses: string[];
  class?: string;
  group?: string;
  batch?: string;
  school?: string;
  phone?: string;
  secondaryEmail?: string;
  hasPassword?: boolean;
  adminPasscode?: string;
  localPassword?: string;
  isDeleted?: boolean;
  createdAt: string;
}

export interface Teacher {
  id: string;
  name: string;
  designation: string;
  info: string;
  photoUrl: string;
}

export interface TeacherAccount {
  id: string;
  username: string;
  password: string;
  name: string;
  designation?: string;
  phone?: string;
  createdAt: string;
  // Permissions
  canCreateCourses: boolean;
  canViewVideoStats: boolean;
  canAccessLiveClasses: boolean;
  canAccessFreeClasses: boolean;
  canAccessStudyPanel: boolean;
  assignedCourseIds: string[]; // empty or IDs, if empty/["all"] means all
}

export interface Course {
  id: string;
  title: string;
  description: string;
  thumbnail: string;
  price: number;
  isFree: boolean;
  category: MainCategory;
  subCategoryId: string;
  syllabusEmbed?: string;
  routineEmbed?: string;
  demoVideoEmbed?: string;
  classEndDate?: string;
  accessEndDate?: string;
  features?: string[];
  accessCode?: string; // Access code for paid courses
  showTeachers: boolean;
  teachers: Teacher[];
  facebookGroupUrl?: string;
  facebookGroupDesc?: string;
  whatsappGroupUrl?: string;
  whatsappGroupDesc?: string;
  telegramGroupUrl?: string;
  telegramGroupDesc?: string;
  discordGroupUrl?: string;
  discordGroupDesc?: string;
  publicGroupUrl?: string;
  publicGroupDesc?: string;
  isHidden?: boolean;
  updatedAt?: string;
  createdAt: string;
}

export interface Subject {
  id: string;
  courseId: string;
  title: string;
  iconUrl?: string;
  order: number;
}

export interface Chapter {
  id: string;
  subjectId: string;
  courseId: string;
  title: string;
  chapterNumber?: string;
  quarter: number;
  order: number;
}

export interface Lesson {
  id: string;
  courseId: string;
  subjectId: string;
  chapterId: string;
  title: string;
  description: string;
  videoUrl: string;
  teacherName: string;
  teacherInfo: string;
  teacherPhoto: string;
  materials: {
    id: string;
    title: string;
    url: string;
  }[];
  isFree?: boolean;
  pdfUrl?: string;
  order: number;
  createdAt: string;
}

export interface CourseResource {
  id: string;
  title: string;
  fileUrl: string;
  type: 'pdf' | 'other';
  order: number;
}

export interface Enrollment {
  id: string;
  userId: string;
  courseId: string;
  status: 'active' | 'pending';
  enrolledAt: string;
}

export interface LiveClass {
  id: string;
  title: string;
  courseId: string; // Link to a specific course
  subjectId?: string;
  chapterId?: string;
  startTime: string;
  status: 'scheduled' | 'live' | 'ended';
  meetingLink: string;
  adminId: string;
}

export interface McqQuestion {
  id: string;
  questionText: string;
  stimulusText?: string; // উদ্দীপক (optional)
  options: string[]; // typically 4 options
  correctOption: number; // 0, 1, 2, 3 (index of correct option)
  explanation?: string; // explanation after student submits (optional)
  images?: string[]; // উদ্দীপক বা প্রশ্নের ছবিসমূহ (max 4)
  isMultipleCompletion?: boolean; // বহুপদী সমাপ্তিসূচক প্রচ্ছন্নতা
  statements?: string[]; // বহুপদী সমাপ্তিসূচক বিবৃতিত্রয় (যেমন: i, ii, iii)
}

export interface Exam {
  id: string;
  title: string;
  description?: string; // নির্দেশাবলী
  category?: string; // category e.g. academic, bcs, hsc
  difficulty?: 'easy' | 'medium' | 'hard';
  link?: string; // Keep as fallback for older Google Forms exams
  duration?: string; // description duration, e.g. "৩০ মিনিট"
  durationMinutes?: number; // strictly timer minutes for the quiz
  totalMarks?: string | number;
  expiryDate?: string;
  subjectName?: string; // optional e.g. "Chemistry"
  chapterName?: string; // optional e.g. "Chapter 3"
  allowMultipleAttempts?: boolean; // default true
  isNegativeMarking?: boolean;
  negativeMarkingValue?: number; // e.g. 0.25
  showResultImmediately?: boolean;
  showCorrectAnswers?: boolean;
  randomizeQuestions?: boolean;
  randomizeOptions?: boolean;
  enableLeaderboard?: boolean;
  enableTimeTracking?: boolean;
  isPublished?: boolean; // default true
  questions?: McqQuestion[]; // list of questions for the quiz
  createdAt: string;
}

export interface ExamSubmission {
  id: string;
  userId: string;
  userName: string;
  score: number;
  totalQuestions: number;
  answers: number[]; // indices of student selected option elements
  timeTakenSeconds: number;
  submittedAt: string;
}

export interface FreeClassSection {
  id: string;
  title: string;
  order: number;
}

export interface FreeClass {
  id: string;
  sectionId: string;
  title: string;
  youtubeUrl: string;
  description?: string;
  order: number;
  createdAt: string;
}

export interface AccessCode {
  id: string;
  code: string;
  courseId: string;
  assignedTo?: string; // User email or UID
  isReusable: boolean;
  maxUsage?: number; // If reusable, how many times?
  currentUsage: number;
  expiryDate?: string;
  createdAt: string;
}

export interface AccessRequest {
  id: string;
  userId: string;
  userEmail: string;
  courseId: string;
  courseTitle: string;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: string;
}

export interface HomepageSettings {
  id: string;
  heroTitle: string;
  heroSubtitle: string;
  schoolImage: string;
  academicImage: string;
  crcImage: string;
  freeClassImage: string;
  freeClassTitle: string;
  freeClassSubtitle: string;
  // Stats
  statsStudents: number;
  statsCourses: number;
  statsTeachers: number;
  statsSuccessRate: number;
  isAutoStats: boolean;
  isAutoStatsV2: boolean;
  isAutoStatsV3: boolean;
  heroButton1Text?: string;
  heroButton2Text?: string;
  showSkillCourses?: boolean;
  skillImage?: string;
  updatedAt: string;
  // Footer settings
  footerDescription?: string;
  footerLocation?: string;
  footerPhone?: string;
  footerEmail?: string;
  footerTradeLicense?: string;
  showFooterLocation?: boolean;
  showFooterPhone?: boolean;
  showFooterEmail?: boolean;
  showFooterTradeLicense?: boolean;
  footerYoutube?: string;
  footerFacebook?: string;
  footerInstagram?: string;
  footerLinks?: { title: string; url: string }[];
  headerMenus?: { id: string; title: string; url: string; isHidden?: boolean }[];
  footerYear?: string;
  footerCopyrightText?: string;
  founderUsername?: string;
  founderPassword?: string;
  websiteLogo?: string;
  adminAccounts?: { id: string; username: string; password: string }[];
  allowedResetEmails?: string[];
  adminCustomPath?: string;
  adminUsername?: string;
  adminPassword?: string;
  adminMainVerifyEmail?: string;
  adminEmails?: { id: string; email: string; isMain: boolean }[];
  googleSheetUrl?: string;
  paymentTutorialVideoUrl?: string;
  paymentSupportWhatsapp?: string;
  externalSupportUrl?: string;
  ziniPayApiKey?: string;
  disablePaymentSystem?: boolean;
  paymentCreateUrl?: string;
  paymentVerifyUrl?: string;
  paymentApiKeyHeader?: string;
  loadingText?: string;
  loadingSpeed?: number;
  loadingAnimation?: 'gradient_spinner' | 'classic_spinner' | 'pulse_ring' | 'dot_bounce';
  tabTitle?: string;
  faviconUrl?: string;
  teacherPanelTitle?: string;
  showTeacherPanelLink?: boolean;
  // SEO Configuration
  seoMode?: 'auto' | 'manual' | 'combined';
  seoCustomRobots?: string;
  seoCustomSitemap?: string;
  seoMetaDescription?: string;
  seoMetaKeywords?: string;
  seoGoogleSearchConsoleMeta?: string;
  siteUrl?: string;
  googleAnalyticsId?: string;
}

export enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LIST = 'list',
  GET = 'get',
  WRITE = 'write',
}

export interface FirestoreErrorInfo {
  error: string;
  operationType: OperationType;
  path: string | null;
  authInfo: {
    userId?: string;
    email?: string | null;
    emailVerified?: boolean;
    isAnonymous?: boolean;
    tenantId?: string | null;
    providerInfo: {
      providerId: string;
      displayName: string | null;
      email: string | null;
      photoUrl: string | null;
    }[];
  }
}
